import { ORDER_STATUSES } from '@ubeya/shared/constants';
import { getReportMethodLabelKey, getReportMethodSourceKey } from '@ubeya/shared/utils/reportMethod';
import { generateShiftName } from '@ubeya/shared/utils/scheduling';
import { SORT_BY_LOCATION_NAME, SORT_BY_POSITION, SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import { PartialNameAggregateRenderer, PartialNameRenderer } from 'timeSheet/components/CellRenderers';
import { NO_AGENCY_FILTER_VALUE } from 'timeSheet/constants';

export const getDistance = (src, dst) => {
  const lat1 = src.lat;
  const lon1 = src.lng;
  const lat2 = dst.lat;
  const lon2 = dst.lng;
  const R = 6371e3; // metres

  const q1 = (lat1 * Math.PI) / 180; // φ, λ in radians
  const q2 = (lat2 * Math.PI) / 180;
  const dq = ((lat2 - lat1) * Math.PI) / 180;
  const dg = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(dq / 2) * Math.sin(dq / 2) + Math.cos(q1) * Math.cos(q2) * Math.sin(dg / 2) * Math.sin(dg / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.abs(R * c) / 1000; // in metres
};

const toAgenciesFilterOptions = ({ agencies, t }) =>
  agencies.size === 0
    ? []
    : [
        { value: NO_AGENCY_FILTER_VALUE, label: t('noAgency') },
        ...[...agencies.values()].sort(SORT_BY_STRING('label'))
      ];

/* on the timesheet page the options are built from the rows themselves, so they follow the selected date range */
export const buildAgenciesFilterOptions = ({ slots = [], t }) => {
  const agencies = new Map();

  slots.forEach(({ isExternal, supplierAccountId, supplierAccountName }) => {
    if (!isExternal || !supplierAccountId || agencies.has(supplierAccountId)) return;

    agencies.set(supplierAccountId, { value: supplierAccountId, label: supplierAccountName || '' });
  });

  return toAgenciesFilterOptions({ agencies, t });
};

export const buildClockSourceFilterOptions = ({ rows = [], t }) => {
  const sources = new Map();

  rows.forEach((row) => {
    (row?.timesheet?.reportMethods || []).forEach((method) => {
      const identity = getReportMethodSourceKey(method);
      const labelKey = getReportMethodLabelKey(method);

      /* create one option per distinct source, skipping methods with no label */
      if (!identity || !labelKey || sources.has(identity)) return;

      sources.set(identity, { value: identity, label: t(labelKey) });
    });
  });

  return [...sources.values()];
};

/* a draft order has not been placed yet and a cancelled one is gone, so neither puts an agency on the event */
const NON_PLACED_ORDER_STATUSES = [ORDER_STATUSES.DRAFT.slug, ORDER_STATUSES.CANCELED.slug];

/* inside an event the options come from the orders placed on it, so every agency ordered from is listed
   even when it has no timesheet rows yet */
export const buildAgenciesFilterOptionsFromOrders = ({ orders = [], mappedSuppliers = {}, t }) => {
  const agencies = new Map();

  orders.forEach(({ status, supplierAccountId }) => {
    if (NON_PLACED_ORDER_STATUSES.includes(status) || !supplierAccountId || agencies.has(supplierAccountId)) return;

    agencies.set(supplierAccountId, {
      value: supplierAccountId,
      label: mappedSuppliers[supplierAccountId]?.name || ''
    });
  });

  return toAgenciesFilterOptions({ agencies, t });
};

export const filterTimesheetBasicFiltersFunction = (item, mappedFilters) => {
  const {
    actualOut,
    breaktimes = [],
    actualIn,
    actualCheckIn,
    isExternal,
    actualCheckOut,
    clientBranchId,
    tags,
    bookerId,
    costCenter,
    supplierData,
    reportMethods
  } = item;

  return (
    (mappedFilters?.tags ? (tags || [])?.some(({ id }) => mappedFilters.tags.includes(id)) : true) &&
    (mappedFilters?.clients ? mappedFilters.clients.includes(clientBranchId) : true) &&
    (mappedFilters?.agencies ? mappedFilters.agencies.includes(supplierData?.id || NO_AGENCY_FILTER_VALUE) : true) &&
    (mappedFilters?.eventBooker ? mappedFilters.eventBooker.includes(bookerId) : true) &&
    (mappedFilters?.withClockIn ? mappedFilters.withClockIn.includes(actualIn ? 1 : 0) : true) &&
    (mappedFilters?.withClockOut ? mappedFilters.withClockOut.includes(actualOut ? 1 : 0) : true) &&
    (mappedFilters?.withCheckIn ? mappedFilters.withCheckIn.includes(actualCheckIn ? 1 : 0) : true) &&
    (mappedFilters?.withCheckout ? mappedFilters.withCheckout.includes(actualCheckOut ? 1 : 0) : true) &&
    (mappedFilters?.withBreaks ? mappedFilters.withBreaks.includes(breaktimes.length > 0 ? 1 : 0) : true) &&
    //general
    (mappedFilters?.locations ? mappedFilters.locations.includes(item.locationId) : true) &&
    (mappedFilters?.positions ? mappedFilters.positions.includes(item.positionId) : true) &&
    (mappedFilters?.approved
      ? mappedFilters.approved.includes(
          isExternal
            ? item.clientApproved || item?.timesheet?.clientApproved
              ? 1
              : 0
            : item.approved || item?.timesheet?.approved
            ? 1
            : 0
        )
      : true) &&
    (mappedFilters?.clientApproval
      ? mappedFilters.clientApproval.includes(item.clientApproved || item?.timesheet?.clientApproved ? 1 : 0)
      : true) &&
    (mappedFilters?.costCenter ? costCenter && mappedFilters.costCenter.includes(costCenter.id) : true) &&
    (mappedFilters?.clockSource
      ? Object.values(reportMethods || {}).some((method) =>
          mappedFilters.clockSource.includes(getReportMethodSourceKey(method))
        )
      : true)
  );
};

export const applyFiltersOnTimesheetData = (data, mappedFilters) => {
  return (data || []).filter((item) => filterTimesheetBasicFiltersFunction(item, mappedFilters));
};

export const generateTimesheetSortingMap = ({
  mappedJobTypes,
  mappedLabels,
  mappedLocations,
  mappedBranches,
  mappedPositions,
  t
}) => ({
  sortByShiftLabel: (a, b) => {
    const aValue = generateShiftName(a, mappedJobTypes, mappedLabels, mappedLocations);
    const bValue = generateShiftName(b, mappedJobTypes, mappedLabels, mappedLocations);

    return aValue?.localeCompare?.(bValue) || 0;
  },

  sortByBranchName: (a, b) => {
    const aValue = mappedBranches[a.branchId]?.name || '';
    const bValue = mappedBranches[b.branchId]?.name || '';

    return aValue?.localeCompare?.(bValue) || 0;
  },
  sortByAreaName: (a, b) => SORT_BY_LOCATION_NAME(a, b, mappedLocations),
  sortByPosition: (a, b) => SORT_BY_POSITION(a, b, mappedPositions, t)
});

// --- Timesheet grid utils (for TimeSheetGridColumns / PayrollTableGrid ag-Grid) ---

export const getLeafChildrenData = (node) => {
  return node?.allLeafChildren?.map((child) => child.data) || [];
};

export const createPartialNameCellRenderer = ({ groupBy, showOnlyFirstName }) => (params) => {
  if (params.node?.group) {
    const groupItems = getLeafChildrenData(params.node);
    return PartialNameAggregateRenderer({
      rowData: { summarized: false, subRows: groupItems },
      columnData: { groupBy, showOnlyFirstName }
    });
  }

  return PartialNameRenderer({
    rowData: params.data,
    columnData: { showOnlyFirstName }
  });
};

export const createNameFieldComparator = ({ fieldName, isGroupedByEmployee }) => (valueA, valueB, nodeA, nodeB) => {
  const isGroupRow = nodeA?.group && nodeB?.group;

  // When grouped by employee - sort the groups themselves
  if (isGroupedByEmployee) {
    if (isGroupRow) {
      const groupItemsA = getLeafChildrenData(nodeA);
      const groupItemsB = getLeafChildrenData(nodeB);

      if (groupItemsA.length > 0 && groupItemsB.length > 0) {
        const nameA = groupItemsA[0]?.employee?.[fieldName]?.toLowerCase() || '';
        const nameB = groupItemsB[0]?.employee?.[fieldName]?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      }
    }
    // Return null to use default sorting for non-group rows when grouped by employee
    return null;
  }

  // When NOT grouped by employee - keep groups in place, sort items within groups
  if (isGroupRow) {
    return 0;
  }

  const nameA = nodeA?.data?.employee?.[fieldName]?.toLowerCase() || '';
  const nameB = nodeB?.data?.employee?.[fieldName]?.toLowerCase() || '';
  return nameA.localeCompare(nameB);
};
